@file:Suppress("MagicNumber", "TooGenericExceptionCaught")

package com.nonopn.skydome.common.app.api

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.bearerAuth
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsBytes
import io.ktor.client.statement.request
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlinx.coroutines.delay
import kotlin.time.Duration.Companion.milliseconds

val listOfCrash = listOf(
    "Request timeout has expired",
    "Unable to resolve"
)

suspend inline fun doRequest(req: () -> HttpResponse): HttpResponse {
    var delay = 100
    var iteration = 0

    while (iteration < 5) {
        try {
            val result = req()

            return result
        } catch (err: Throwable) {
            val message = err.message ?: ""

            if (iteration >= 4 || !listOfCrash.any { message.contains(it) }) {
                throw err
            }
        }

        delay(delay.milliseconds)
        delay *= 2
        iteration++
    }

    return req()
}

@Suppress("SwallowedException", "MagicNumber")
suspend inline fun <reified O> manageAnswer(req: () -> HttpResponse): O {
    val request = doRequest(req)

    return request.status.value.let {
        if (it in 200..299) {
            return@let request.body()
        }

        val errorFromBackend: String = request.body()
        throw BackendErrorException(
            "Error for ${request.request.url}",
            errorFromBackend,
            request.status
        )
    }
}

data class DoNetworkCalls(
    val client: HttpClient
) {
    suspend inline fun <reified T> get(
        url: String
    ): T = manageAnswer { client.get(url) }

    suspend inline fun getBytes(url: String): ByteArray {
        val request = client.get(url)
        return request.status.value.let {
            if (it in 200..299) {
                return@let request.bodyAsBytes()
            }

            val errorFromBackend: String = request.body()
            throw BackendErrorException(
                "Error",
                errorFromBackend,
                request.status
            )
        }
    }

    suspend inline fun <reified O, T> get(
        url: String,
        mapResult: (result: O) -> T
    ): T {
        val result: O = manageAnswer { client.get(url) }

        return mapResult(result)
    }

    suspend inline fun <reified O, T> post(
        url: String,
        mapResult: (result: O) -> T
    ): T {
        val result: O = manageAnswer { client.post(url) }

        return mapResult(result)
    }

    suspend inline fun <reified T> delete(
        url: String
    ): T = manageAnswer { client.delete(url) }

    suspend inline fun <reified I, reified O> delete(
        url: String,
        body: I
    ): O = manageAnswer {
        client.delete(url) {
            contentType(ContentType.Application.Json)
            setBody(body)
        }
    }

    suspend inline fun <reified T> post(
        url: String
    ): T = manageAnswer { client.post(url) }

    suspend inline fun <reified T> put(
        url: String
    ): T = manageAnswer { client.put(url) }

    suspend inline fun <reified I, reified O, T> post(
        url: String,
        body: I,
        mapResult: (result: O) -> T
    ): T {
        val result: O = manageAnswer {
            client.post(url) {
                contentType(ContentType.Application.Json)
                setBody(body)
            }
        }

        return mapResult(result)
    }

    suspend inline fun <reified I, reified O, T> put(
        url: String,
        body: I,
        mapResult: (result: O) -> T
    ): T {
        val result: O = manageAnswer {
            client.put(url) {
                contentType(ContentType.Application.Json)
                setBody(body)
            }
        }

        return mapResult(result)
    }

    suspend inline fun <reified I, reified O> post(
        url: String,
        body: I
    ): O = manageAnswer {
        client.post(url) {
            contentType(ContentType.Application.Json)
            setBody(body)
        }
    }

    suspend inline fun <reified I, reified O> put(
        url: String,
        body: I
    ): O = manageAnswer {
        client.put(url) {
            contentType(ContentType.Application.Json)
            setBody(body)
        }
    }

    suspend inline fun <reified I, reified O> post(
        url: String,
        optionalBearer: String? = null,
        body: I
    ): O = manageAnswer {
        client.post(url) {
            if (null != optionalBearer) {
                // option to override the bearer for this client
                bearerAuth(optionalBearer)
            }

            contentType(ContentType.Application.Json)
            setBody(body)
        }
    }

    // Post without deserializing the response body (avoids unknown-key JSON errors).
    // Throws BackendErrorException for non-2xx responses; ignores body on success.
    suspend inline fun <reified I> postVoid(url: String, body: I) {
        val request = doRequest {
            client.post(url) {
                contentType(ContentType.Application.Json)
                setBody(body)
            }
        }
        if (request.status.value !in 200..299) {
            val errorFromBackend: String = request.body()
            throw BackendErrorException(
                "Error for ${request.request.url}",
                errorFromBackend,
                request.status
            )
        }
    }
}
